import styled, { CSSProperties, AnyStyledComponent } from 'styled-components';
import React, { FC } from 'react';
import COLORS from '../../style/universal.color';
import { components as SelectComponents } from 'react-select';
import Icon from '../Icon/icon';

const {
  mlbBlack,
  mlbWhite,
  mlbGrey1,
  mlbGrey2,
  mlbGrey3,
  mlbGrey4,
  mlbGrey5,
  mlbGrey6,
  mlbGrey7,
  inputInvalid,
  inputInvalidOnDark,
  transparent
} = COLORS;

interface RequiredWrapperProps {
  valueSelected: boolean;
  dark: boolean;
  isDisabled: boolean;
};

export const RequiredWrapper: AnyStyledComponent = styled.div`
  font-size: 10px;
  letter-spacing: .28;
  color: ${({ valueSelected, dark, isDisabled }: RequiredWrapperProps) => {
    if (valueSelected || isDisabled) {
      return dark ? mlbGrey3 : mlbGrey4;
    } else {
      return dark ? inputInvalidOnDark : inputInvalid;
    }
  }};
  margin-top: 3px;
`

// A fleshed out view of what we're returning in the lower-level component states
export interface DropdownLowerState {
  isSelected?: boolean;
  isFocused?: boolean;
  isDisabled?: boolean;
  selectProps?: any;
}


// This is a custom styles object to be injested by the react-select component
// It, of course, isn't styled components but it follows a similar structure
// using props passed
export const DesktopCustomStyles: object = {
  // styling for each option in the menu
  option: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      isSelected,
      isFocused,
      selectProps
    } = state;

    const {
      baseTextColor,
      highlightColor,
      isMulti,
      hoverColor,
      fontFamily
    } = selectProps;

    return {
      ...base,
      display: 'flex',
      alignItems: 'center',
      cursor: 'pointer',
      overflowX: 'hidden',
      width: '100%',
      textOverflow: 'ellipsis',
      height: '32px',
      lineHeight: 1,
      fontSize: '13px',
      color: (!isMulti && isSelected) ? highlightColor : baseTextColor,
      fontFamily,
      backgroundColor: isFocused ? hoverColor : '',
      '&:hover': {
        backgroundColor: hoverColor
      },
      paddingRight: isMulti ? '40px' : '',
      position: 'relative',
      svg: {
        position: 'absolute',
        right: '15px',
        width: '12px',
        height: '12px',
        top: '6px',
        backgroundColor: isSelected ? highlightColor : transparent,
        borderRadius: '2px',
        border: `1px solid ${isSelected ? highlightColor : mlbGrey3}`,
        padding: '3px'
      }
    };
  },
  // styling for the main control button space
  control: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps,
      isFocused,
      isDisabled
    } = state;

    const {
      width,
      dark,
      highlightColor,
      baseTextColor,
      invalidRequired,
      fontFamily
    } = selectProps;

    let borderColor: string;
    if (isDisabled) {
      borderColor = dark ? mlbGrey6 : mlbGrey2;
    } else if (invalidRequired) {
      borderColor = dark ? inputInvalidOnDark : inputInvalid;
    } else {
      borderColor = dark ? mlbGrey4 : mlbGrey2;
    }
    return {
      ...base,
      cursor: 'pointer',
      borderWidth: '1px',
      borderRadius: '6px',
      borderColor: borderColor,
      outline: 'none',
      color: isDisabled ? (dark ? mlbGrey6 : mlbGrey3) : (isFocused ? highlightColor : baseTextColor),
      fontSize: '13px',
      fontFamily,
      fontWeight: 'semibold',
      minHeight: '34px',
      width: width,
      backgroundColor: dark ? mlbGrey7 : (isDisabled ? mlbGrey1 : mlbWhite),
      boxShadow: 'none',
      '&:hover': {
        borderColor: mlbGrey3
      },
      '&:focus': {
        outline: 'none'
      }
    };
  },
  // styling for the indicator separator. Sets it to display none
  indicatorSeparator: () => ({
    display: 'none'
  }),
  // styling for the menu that appears when open
  menu: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps
    } = state;

    const {
      dark,
      menuWidth
    } = selectProps;

    return {
      ...base,
      overflowX: 'hidden',
      marginTop: '4px',
      radius: '6px',
      width: menuWidth,
      border: dark ? `1px solid ${ mlbGrey5 }` : `1px solid ${ mlbGrey1 }`,
      backgroundColor: dark ? mlbBlack : mlbWhite
    };
  },
  // styling for the placeholder text
  placeholder: (base: CSSProperties) => ({
    ...base,
    color: 'inherit'
  }),
  // styling for the single value container text
  singleValue: (base: CSSProperties) => ({
    ...base,
    color: 'inherit'
  }),
  // styling for the group heading in the menus
  groupHeading: (base: CSSProperties) => ({
    ...base,
    fontSize: '10px'
  }),
  // styling for the group in the menus
  group: (base: CSSProperties) => ({
    ...base,
    padding: '4px 0px'
  }),
  // styling for the menu list in the menus
  menuList: (base: CSSProperties) => ({
    ...base,
    paddingTop: '0',
    paddingBottom: '0'
  }),
  // styling for the multiValue containers
  multiValue: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps
    } = state;

    const {
      multiValueColor
    } = selectProps;

    return {
      ...base,
      height: '24px',
      backgroundColor: multiValueColor
    };
  },
  // styling for the label in the multiValue containers
  multiValueLabel: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps
    } = state;

    const {
      multiValueFontColor
    } = selectProps;

    return {
      ...base,
      fontSize: '11px',
      lineHeight: '19px',
      fontWeight: 'bold',
      paddingRight: '0px',
      color: multiValueFontColor
    };
  },
  // styling for the remove button in multiValues
  multiValueRemove: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps
    } = state;

    const {
      multiValueFontColor
    } = selectProps;

    return {
      ...base,
      backgroundColor: transparent,
      svg: {
        path: {
          fill: multiValueFontColor
        }
      },
      '&:hover': {
        backgroundColor: transparent
      }
    };
  },
  // styling for the dropdown icon container
  indicatorsContainer: (base: CSSProperties, state: DropdownLowerState) => {
    const {
      selectProps
    } = state;

    const {
      menuIsOpen
    } = selectProps;

    const orientation: string = menuIsOpen ? 'rotate(180deg)' : 'rotate(0deg)';

    return {
      ...base,
      transform: orientation,
      height: '34px',
      svg: {
        path: {
          fill: mlbGrey4
        }
      }
    };
  }
};

// A custom option object to be used by react-select so we can render checkmark icons to the right
// in multi-selects
export const CustomOption: FC = (props: any) => {

  const { selectProps = {}, isSelected = false } = props;
  const { isMulti = false } = selectProps;

  return (
    <div >
      <SelectComponents.Option {...props}>
        {props.label}
        { isMulti && (isSelected ? <Icon name='checkmark' fill={mlbWhite} /> : <svg/>)}
      </SelectComponents.Option>

    </div>
  );
};

// A custom indicator object to be used by react-select so we can render our caret icon
export const CustomIndicator: FC = (props: any) => {
  return (
    <SelectComponents.DropdownIndicator {...props}>
      <Icon name={'caret-down'} width={ 11 } />
    </SelectComponents.DropdownIndicator>
  );
};

export const Wrapper = styled.div``;
